import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Shared behaviour for the option menus that open INSIDE an activity card
// (matching rows, inline dropdown pills, media dropdowns). The card scrolls on
// its own, which is what these menus kept getting wrong on short laptop screens:
//
//  - Outside clicks are caught with a document listener, never a full-screen
//    fixed scrim: a fixed layer also swallows the mouse wheel, so the card could
//    not scroll while a menu was open and the lower rows stayed out of reach.
//  - Up or down is decided from the menu's REAL rendered height against the
//    card's scroller (clamped to the window), not the window alone.
//  - Once open, the menu is scrolled into view — vertically only, so a wide
//    menu on a phone never drags the card sideways.

export const findScrollParent = (
  node: HTMLElement | null,
): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const visibleBounds = (scroller: HTMLElement | null) => {
  const rect = scroller
    ? scroller.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight };
  return {
    top: Math.max(rect.top, 0),
    bottom: Math.min(rect.bottom, window.innerHeight),
  };
};

// Flip up only when the menu does not fit below AND fits entirely above. A
// menu that fits neither way opens DOWN: overflow below the card extends its
// scroll area and stays reachable, overflow above it can never be scrolled to.
// `bounds` is the visible area (scroller clamped to the window).
export const shouldOpenMenuUp = (
  anchor: { top: number; bottom: number },
  bounds: { top: number; bottom: number },
  menuHeight: number,
): boolean => {
  const spaceBelow = bounds.bottom - anchor.bottom;
  const spaceAbove = anchor.top - bounds.top;
  return spaceBelow < menuHeight && spaceAbove >= menuHeight;
};

// Pixels to scroll so `rect` sits inside `bounds` (0 when it already does).
export const verticalScrollDelta = (
  rect: { top: number; bottom: number },
  bounds: { top: number; bottom: number },
): number => {
  if (rect.top < bounds.top) return rect.top - bounds.top;
  if (rect.bottom > bounds.bottom) {
    // Never push the menu's top out of view to show its bottom.
    return Math.min(rect.bottom - bounds.bottom, rect.top - bounds.top);
  }
  return 0;
};

const CLOSED = Symbol("closed");

// `openKey` identifies WHICH item is open (row key, blank index, or `true`);
// null / undefined / false mean closed. Switching straight from one item to
// another (e.g. by keyboard, with no outside press in between) re-places and
// re-scrolls the new menu because the key changes.
const useAnchoredMenu = <A extends HTMLElement, M extends HTMLElement>(
  openKey: unknown,
  onClose: () => void,
) => {
  const isOpen = openKey != null && openKey !== false;
  const [openUp, setOpenUp] = useState(false);
  // Attach to the element wrapping the trigger AND the menu of the OPEN item.
  const anchorRef = useRef<A | null>(null);
  const menuRef = useRef<M | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onCloseRef.current();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Place, then reveal — in ONE effect, so the reveal never measures the menu
  // where it sat before a flip (that scrolled the card for nothing). Placement
  // happens once per opened item: when it changes `openUp`, this returns and
  // the re-render runs the effect again, which now only reveals. Placing once
  // also stops a flip from re-deciding itself when the flip changes whether
  // the card overflows at all.
  const placedForRef = useRef<unknown>(CLOSED);
  useLayoutEffect(() => {
    if (!isOpen) {
      placedForRef.current = CLOSED;
      return;
    }
    const menu = menuRef.current;
    if (!menu) return;
    if (placedForRef.current !== openKey) {
      placedForRef.current = openKey;
      const anchor = anchorRef.current;
      const up = anchor
        ? shouldOpenMenuUp(
            anchor.getBoundingClientRect(),
            visibleBounds(findScrollParent(anchor)),
            menu.offsetHeight,
          )
        : openUp;
      if (up !== openUp) {
        setOpenUp(up);
        return;
      }
    }
    const scroller = findScrollParent(menu);
    const delta = verticalScrollDelta(
      menu.getBoundingClientRect(),
      visibleBounds(scroller),
    );
    if (delta === 0) return;
    if (scroller) scroller.scrollTop += delta;
    else window.scrollBy(0, delta);
  }, [isOpen, openKey, openUp]);

  return { anchorRef, menuRef, openUp };
};

export default useAnchoredMenu;
